import posthog from "posthog-js";
import type { CaptureResult } from "posthog-js";

import config from "./config";
import { redactLogRecord } from "./logRedaction";
import type { User } from "./auth";

export const ENVIRONMENT = config.ENV;

const token = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN as string | undefined;
const apiHost =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  "https://us.i.posthog.com";
const uiHost =
  (import.meta.env.VITE_POSTHOG_UI_HOST as string | undefined) ??
  "https://us.posthog.com";

const apiHostnames = [config.API_HOST, "http://localhost:8000"]
  .map((h) => h.replace(/^https?:\/\//, ""))
  .filter(Boolean);

/** Where in the app an event happened, derived from the URL path. */
export interface RouteContext {
  /** Route pattern with ids collapsed, e.g. `/app/jobs/:id`. */
  route: string;
  /** Product area, e.g. `jobs`, `reservations`, `signup`, `public`. */
  section: string;
}

type RouteResolver = (pathname: string) => RouteContext | undefined;

let resolveRoute: RouteResolver | undefined;

/**
 * Registered by `./router.tsx`, which owns the route table. A setter rather than an import
 * because the router imports every page, and the pages import this module.
 */
export function setRouteResolver(resolver: RouteResolver): void {
  resolveRoute = resolver;
}

/**
 * Stamp `route` and `section` on every event. Raw URLs carry record ids
 * (`/app/jobs/8f1c...`), so they cannot be grouped or broken down by; the pattern can.
 */
export function addRouteContext(
  event: CaptureResult | null
): CaptureResult | null {
  if (!event || event.event === "$snapshot" || !resolveRoute) return event;
  try {
    const pathname =
      (event.properties?.$pathname as string | undefined) ??
      window.location.pathname;
    const context = resolveRoute(pathname);
    if (context) {
      event.properties = { ...event.properties, ...context };
    }
  } catch {
    // Enrichment is best-effort: never drop an event over it.
  }
  return event;
}

if (token) {
  posthog.init(token, {
    api_host: apiHost,
    ui_host: uiHost,
    defaults: "2026-05-30",
    capture_pageview: "history_change",
    capture_pageleave: true,
    capture_exceptions: true,
    // Clicks, rage clicks, mouse movement and scroll depth for the Heatmaps product. Set here
    // rather than left to the project toggle so it cannot be switched off from the UI by
    // accident. Heatmap data is coordinates only — no text — so the replay masking below
    // does not apply to it and is not needed.
    capture_heatmaps: true,
    before_send: addRouteContext,
    person_profiles: "identified_only",
    tracing_headers: apiHostnames,
    logs: {
      // Every console.* call in this app and in its dependencies becomes a log record.
      // beforeSend is not optional alongside this: it is the only thing standing between a
      // stray console.log of a token and PostHog. See ./logRedaction.ts.
      captureConsoleLogs: true,
      beforeSend: redactLogRecord,
      // Without these, web logs arrive as "unknown_service" and do not line up with the
      // tsg-web spans from ./tracing.ts.
      serviceName: "tsg-web",
      environment: ENVIRONMENT,
      serviceVersion: config.SHA,
    },
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: { password: true },
      // Mask all rendered text, not just inputs. TSG pages (CustomersPage,
      // JobsDetailsPage, ReservationsDetailsPage, EmployeesPage, etc.) render
      // shop customers' and employees' names/phones/emails/addresses as plain
      // text well beyond form inputs, and that PII isn't ours to expose in
      // session replay. A `.ph-mask` opt-in selector would require finding and
      // tagging every such surface (20+ files at last count) with no
      // enforcement to keep new ones covered, so mask-by-default is the safer
      // choice; unmask specific non-sensitive elements with `.ph-no-capture`-
      // style opt-outs (`maskTextSelector` accepts a `:not()` refinement) if
      // needed later.
      maskTextSelector: "*",
    },
    loaded: (ph) => {
      ph.register({
        environment: ENVIRONMENT,
        release: config.SHA,
        app: "web",
      });
      if (ENVIRONMENT === "local") {
        ph.set_config({ disable_session_recording: true });
      }
    },
  });
}

export function identifyPostHogUser(user: User): void {
  const name = `${user.given_name} ${user.family_name}`.trim() || user.email;
  const personProperties = {
    email: user.email,
    name,
    role: user.role,
    account_id: user.account_id,
    employee_id: user.employee_id,
    email_verified: user.email_verified,
    environment: ENVIRONMENT,
  };
  if (posthog.get_distinct_id() !== user.sub) {
    posthog.identify(user.sub, personProperties);
  } else {
    posthog.setPersonProperties(personProperties);
  }
  if (user.account_id) {
    posthog.group("account", user.account_id, { environment: ENVIRONMENT });
  }
  // Support-widget identity verification: the API signs `sub` with the project
  // secret and ships it in the JWT; PostHog verifies it server-side so support
  // tickets are tied to the real user rather than an anonymous browser session.
  // https://posthog.com/docs/support/widget#identity-verification
  if (user.posthog_identity_hash) {
    posthog.setIdentity(user.sub, user.posthog_identity_hash);
  }
}

export function resetPostHog(): void {
  posthog.clearIdentity();
  posthog.reset();
}

export default posthog;
